import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import theme from '../../../theme';

const clearIconSvg = (
  <svg
    width={theme.sizes.icon}
    height={theme.sizes.icon}
    viewBox="0 0 19 19"
    stroke={theme.colors.textHint}
    strokeWidth="2"
    fill="none"
  >
    <line x1="4" y1="4" x2="15" y2="15" />
    <line x1="15" y1="4" x2="4" y2="15" />
  </svg>
);

const dropdownIconSvg = (
  <svg
    width={theme.sizes.icon}
    height={theme.sizes.icon}
    viewBox="0 0 19 19"
    stroke={theme.colors.textHint}
    strokeWidth="2"
    fill="none"
  >
    <polyline points="4,7 9.5,13 15,7" />
  </svg>
);

const indicatorButtonStyle = {
  padding: theme.spacing.iconPadding,
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
};

function ClearIndicator({ innerProps }) {
  return (
    <button
      type="button"
      ref={innerProps.ref}
      onMouseDown={innerProps.onMouseDown}
      onTouchEnd={innerProps.onTouchEnd}
      style={indicatorButtonStyle}
      aria-label="Clear selection"
    >
      {clearIconSvg}
    </button>
  );
}

ClearIndicator.propTypes = {
  innerProps: PropTypes.shape({
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    onMouseDown: PropTypes.func,
    onTouchEnd: PropTypes.func,
  }).isRequired,
};

function DropdownIndicator({ innerProps }) {
  return (
    <button
      type="button"
      ref={innerProps.ref}
      onMouseDown={innerProps.onMouseDown}
      onTouchEnd={innerProps.onTouchEnd}
      style={indicatorButtonStyle}
      aria-label="Toggle menu"
    >
      {dropdownIconSvg}
    </button>
  );
}

DropdownIndicator.propTypes = {
  innerProps: PropTypes.shape({
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    onMouseDown: PropTypes.func,
    onTouchEnd: PropTypes.func,
  }).isRequired,
};

const customComponents = { ClearIndicator, DropdownIndicator };

const customStyles = {
  // Above the table's sticky first column, which is positioned and so paints
  // over an unraised menu — the same layer the date picker popover uses.
  menu: (baseStyles) => ({
    ...baseStyles,
    zIndex: theme.zIndices.dropdown,
  }),
  control: (baseStyles, state) => ({
    ...baseStyles,
    borderRadius: theme.radii.input,
    border: state.isFocused
      ? `1.5px solid ${theme.colors.infoBlue}`
      : theme.borders.input,
    boxShadow: state.isFocused
      ? `0 0 0 2px ${theme.colors.infoBlue}33`
      : 'none',
    minHeight: theme.sizes.selectMinHeight,
    transition: theme.transitions.default,
    '&:hover': {
      borderColor: theme.colors.infoBlue,
    },
  }),
  menu: (baseStyles) => ({
    ...baseStyles,
    zIndex: theme.zIndices.dropdown,
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    ...theme.typography.body,
    color: theme.colors.textHint,
  }),
  input: (baseStyles) => ({
    ...baseStyles,
    ...theme.typography.body,
    color: theme.colors.textBody,
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    ...theme.typography.body,
    color: theme.colors.textBody,
  }),
  clearIndicator: (baseStyles) => ({
    ...baseStyles,
    padding: theme.spacing.iconPadding,
    cursor: 'pointer',
    '&:hover svg': {
      stroke: theme.colors.infoBlue,
    },
  }),
  dropdownIndicator: (baseStyles) => ({
    ...baseStyles,
    padding: theme.spacing.iconPadding,
    cursor: 'pointer',
    '&:hover svg': {
      stroke: theme.colors.infoBlue,
    },
  }),
};

function SingleSelect({
  options,
  value,
  onChange,
  isLoading,
  isDisabled,
  width,
  inputId,
  placeholder,
  isClearable,
  'data-testid': dataTestId,
}) {
  // Memoized so react-select isn't handed a fresh styles object (forcing a
  // style recompute) on every parent re-render.
  const mergedStyles = useMemo(
    () => ({
      ...customStyles,
      control: (baseStyles, state) => {
        const themed = customStyles.control(baseStyles, state);
        return {
          ...themed,
          ...(width ? { width } : {}),
        };
      },
    }),
    [width],
  );

  return (
    <div data-testid={dataTestId}>
      <Select
        options={options}
        value={value}
        onChange={onChange}
        isLoading={isLoading}
        isDisabled={isDisabled}
        isMulti={false}
        isSearchable
        isClearable={isClearable}
        inputId={inputId}
        placeholder={placeholder}
        components={customComponents}
        styles={mergedStyles}
      />
    </div>
  );
}

SingleSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ).isRequired,
  value: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  width: PropTypes.string,
  inputId: PropTypes.string,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  'data-testid': PropTypes.string,
};

SingleSelect.defaultProps = {
  value: null,
  isLoading: false,
  isDisabled: false,
  width: null,
  inputId: undefined,
  placeholder: undefined,
  isClearable: false,
  'data-testid': undefined,
};

export default SingleSelect;
